import { useCallback, useEffect, useRef, useState } from "react"
import { supabase } from "./supabase"
import { useAuth } from "../components/AuthProvider"
import { getDefaultWaypointPaths, sanitizeWaypointPaths } from "./waypoints"

type FavoritesRow = {
  id: string
  user_id: string
  company_id: string
  paths: string[]
  updated_at: string
}

type UseManifestFavoritesResult = {
  favorites: string[]
  loading: boolean
  toggle: (path: string) => Promise<void>
  setFavorites: (paths: string[]) => Promise<void>
}

/** Event name for cross-component sync (e.g. sidebar updates when settings change). */
export const PORTAL_FAVORITES_UPDATED = "portal-favorites-updated"
type PortalFavoritesUpdatedDetail = { paths?: string[] }

export function useManifestFavorites(): UseManifestFavoritesResult {
  const { user, companyId, role, isCreator } = useAuth()
  const [favorites, setLocalFavorites] = useState<string[]>([])
  const favoritesRef = useRef<string[]>([])
  const fetchRequestRef = useRef(0)
  const [loading, setLoading] = useState(true)

  // keep ref in sync so toggle closure always sees latest value
  useEffect(() => {
    favoritesRef.current = favorites
  }, [favorites])

  const fetchFavorites = useCallback(async (silent = false) => {
    const context = { role, isCreator }
    const defaultPaths = getDefaultWaypointPaths(context)
    if (!user || !companyId) {
      setLocalFavorites(defaultPaths)
      setLoading(false)
      return
    }
    const requestId = ++fetchRequestRef.current
    if (!silent) setLoading(true)
    try {
      const { data, error } = await supabase
        .from("portal_favorites")
        .select("paths")
        .eq("user_id", user.id)
        .eq("company_id", companyId)
        .maybeSingle()
      if (error) throw error
      if (requestId !== fetchRequestRef.current) return
      if (data) {
        const safePaths = sanitizeWaypointPaths(data.paths ?? [], context)
        const shouldBootstrapDefaults = safePaths.length === 0
        const nextPaths = shouldBootstrapDefaults ? defaultPaths : safePaths
        setLocalFavorites(nextPaths)
        if ((data.paths ?? []).length !== nextPaths.length || shouldBootstrapDefaults) {
          await supabase
            .from("portal_favorites")
            .upsert(
              { user_id: user.id, company_id: companyId, paths: nextPaths },
              { onConflict: "user_id,company_id" }
            )
        }
      } else {
        setLocalFavorites(defaultPaths)
        await supabase
          .from("portal_favorites")
          .upsert(
            { user_id: user.id, company_id: companyId, paths: defaultPaths },
            { onConflict: "user_id,company_id" }
          )
      }
    } catch (error) {
      if (requestId !== fetchRequestRef.current) return
      console.error("[useManifestFavorites] Failed to fetch favorites", error)
    } finally {
      if (requestId === fetchRequestRef.current) {
        setLoading(false)
      }
    }
  }, [user?.id, companyId, role, isCreator])

  useEffect(() => {
    fetchFavorites()
  }, [fetchFavorites])

  // When another instance (e.g. Settings) closes the picker, sync immediately.
  useEffect(() => {
    const onUpdated = (evt: Event) => {
      const customEvent = evt as CustomEvent<PortalFavoritesUpdatedDetail>
      const incoming = customEvent.detail?.paths
      const context = { role, isCreator }

      if (Array.isArray(incoming)) {
        setLocalFavorites(sanitizeWaypointPaths(incoming, context))
        return
      }

      if (user?.id && companyId) {
        fetchFavorites(true)
      } else {
        setLocalFavorites(getDefaultWaypointPaths(context))
      }
    }
    window.addEventListener(PORTAL_FAVORITES_UPDATED, onUpdated)
    return () => window.removeEventListener(PORTAL_FAVORITES_UPDATED, onUpdated)
  }, [user?.id, companyId, fetchFavorites, role, isCreator])

  const persist = useCallback(
    async (paths: string[]) => {
      if (!user || !companyId) return
      await supabase
        .from("portal_favorites")
        .upsert(
          { user_id: user.id, company_id: companyId, paths },
          { onConflict: "user_id,company_id" }
        )
      // Don't dispatch here — sidebar would refetch on every toggle and jump while editing.
      // Call notifyPortalFavoritesUpdated() when the edit modal closes instead.
    },
    [user?.id, companyId]
  )

  const toggle = useCallback(
    async (path: string) => {
      const context = { role, isCreator }
      const allowed = new Set(sanitizeWaypointPaths([path], context))
      if (!allowed.has(path)) return

      const next = favoritesRef.current.includes(path)
        ? favoritesRef.current.filter((p) => p !== path)
        : [...favoritesRef.current, path]
      const safeNext = sanitizeWaypointPaths(next, context)
      setLocalFavorites(safeNext)
      await persist(safeNext)
    },
    [persist, role, isCreator]
  )

  const setFavorites = useCallback(
    async (paths: string[]) => {
      const safePaths = sanitizeWaypointPaths(paths, { role, isCreator })
      setLocalFavorites(safePaths)
      await persist(safePaths)
    },
    [persist, role, isCreator]
  )

  return { favorites, loading, toggle, setFavorites }
}

/** Call after the user closes the favorites picker so the sidebar refetches and glitches once. */
export function notifyPortalFavoritesUpdated(paths?: string[]): void {
  window.dispatchEvent(new CustomEvent<PortalFavoritesUpdatedDetail>(PORTAL_FAVORITES_UPDATED, { detail: { paths } }))
}
